import { faXmark } from "@fortawesome/pro-solid-svg-icons";
import { Button } from "@progress/kendo-react-buttons";
import { FieldWrapper, Form, FormElement } from "@progress/kendo-react-form";
import { Checkbox } from "@progress/kendo-react-inputs";
import { Error, Hint, Label } from "@progress/kendo-react-labels";
import { useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { allDatasets } from "../Common/Store/CommonSignals";
import { ColorChangingIcon } from "../Common/UI/ColorChangingIcon";
import { CustomPeoplePicker } from "../Common/UI/CustomPeoplePicker";
import { templateData } from "../Template/NewTemplateSignals";

const SharedPerms = ({
  isDataset,
  coOwners,
  removeCoOwner,
  removeSharedPermission,
  sharedPermission,
  closeWindow,
}) => {
  const [isCoOwner, setIsCoOwner] = useState(false);
  const [canModify, setCanModify] = useState(false);
  const [canView, setCanView] = useState(false);
  const [canMakeEntry, setCanMakeEntry] = useState(false);
  const [canViewAuthorOnly, setCanViewAuthorOnly] = useState(false);
  const [selectedPeople, setSelectedPeople] = useState([]);
  const [existingPermissionErrorMessage, setExistingPermissionErrorMessage] = useState(null);
  const singleSelect = false;

  // Helper function to check if a user belongs to any group in permissions
  const userBelongsToGroup = (email, groupsInPermissions) => {
    return groupsInPermissions.some((groupPerm) => {
      const groupDataset = allDatasets.value?.find(
        (ds) => ds.datasetType === "User Group" && ds.id === groupPerm.userGroupId
      );

      if (!groupDataset) return false;

      const groupMembers =
        groupDataset.values
          ?.map((v) => {
            try {
              const person = typeof v.value === "string" ? JSON.parse(v.value) : v.person;
              return person?.email?.toLowerCase();
            } catch {
              return null;
            }
          })
          .filter(Boolean) || [];

      return groupMembers.includes(email.toLowerCase());
    });
  };

  const submitDisabled = () => {
    const peopleList = selectedPeople?.peoplePicker || [];
    const selectedGroup = selectedPeople?.peoplePicker_group || null;
    if (peopleList.length === 0 && !selectedGroup) {
      return true;
    }
    if (!isCoOwner && !canModify && !canView && !canMakeEntry && !canViewAuthorOnly) {
      return true;
    }
    return false;
  };

  const getPermissionType = () => {
    if (canModify) return "Modify";
    if (canView) return "View";
    if (canViewAuthorOnly) return "ViewAuthorOnly";
    return "MakeEntry";
  };

  const getGroupPermission = (selectedGroup, permissionType) => {
    const groupEntity = selectedGroup.displayName;
    const groupId = selectedGroup.groupId;
    const groupMembers = selectedGroup.members || [];

    const existsInShared = sharedPermission?.some(
      (sp) => sp.isUserGroup && sp.userGroupId === groupId
    );
    const existsInCoOwners = coOwners?.some((co) => co.isUserGroup && co.userGroupId === groupId);

    if (existsInShared || existsInCoOwners) {
      return null;
    }

    // Get emails of group members to remove them from existing permissions
    const memberEmails = groupMembers.map((member) => member.email?.toLowerCase()).filter(Boolean);

    // Remove individual members from coOwners and sharedPermission
    const updatedCoOwners =
      coOwners?.filter((co) => co.isUserGroup || !memberEmails.includes(co.user.toLowerCase())) ||
      [];
    const updatedSharedPermission =
      sharedPermission?.filter(
        (sp) => sp.isUserGroup || !memberEmails.includes(sp.entity.toLowerCase())
      ) || [];

    let newPermission = { coOwner: null, sharedPermission: null };

    if (isCoOwner) {
      newPermission.coOwner = [
        {
          user: groupEntity,
          isUserGroup: true,
          userGroupId: groupId,
        },
      ];
      newPermission.updatedCoOwners = updatedCoOwners;
      newPermission.updatedSharedPermission = updatedSharedPermission;
    } else {
      newPermission.sharedPermission = [
        {
          entity: groupEntity,
          permission: permissionType,
          isUserGroup: true,
          userGroupId: groupId,
        },
      ];
      newPermission.updatedCoOwners = updatedCoOwners;
      newPermission.updatedSharedPermission = updatedSharedPermission;
    }
    return newPermission;
  };

  const getIndividualPermission = (peopleList, permissionType) => {
    // Regular user selection - add individual users
    // First, check if any user belongs to a group already in permissions
    const allGroupsInPermissions = [
      ...(coOwners?.filter((co) => co.isUserGroup) || []),
      ...(sharedPermission?.filter((sp) => sp.isUserGroup) || []),
    ];

    const filteredPeople = peopleList.filter((person) => {
      const email = person.email?.toLowerCase();
      if (!email) return false;

      // Check if already exists as individual user
      const existsInShared = sharedPermission?.some(
        (sp) => !sp.isUserGroup && sp.entity.toLowerCase() === email
      );
      const existsInCoOwners = coOwners?.some(
        (co) => !co.isUserGroup && co.user.toLowerCase() === email
      );

      if (existsInShared || existsInCoOwners) {
        return false;
      }

      // Check if user belongs to any group already in permissions
      return !userBelongsToGroup(email, allGroupsInPermissions);
    });

    if (filteredPeople.length === 0) {
      return null;
    }

    return {
      coOwner: isCoOwner
        ? filteredPeople.map((person) => ({
            user: person.email.toLowerCase(),
            isUserGroup: false,
          }))
        : null,

      sharedPermission: !isCoOwner
        ? filteredPeople.map((person) => ({
            entity: person.email.toLowerCase(),
            permission: permissionType,
            isUserGroup: false,
          }))
        : null,
    };
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const peopleList = selectedPeople?.peoplePicker || [];
    const selectedGroup = selectedPeople?.peoplePicker_group || null;

    if (peopleList.length === 0 && !selectedGroup) {
      return;
    }

    const permissionType = getPermissionType();
    let newPermission = null;

    if (selectedGroup) {
      newPermission = getGroupPermission(selectedGroup, permissionType);
    } else {
      newPermission = getIndividualPermission(peopleList, permissionType);
    }

    if (newPermission) {
      console.log("Form submitted: ", {
        newPermission,
      });
      setExistingPermissionErrorMessage(null);
      closeWindow(newPermission);
    } else if (selectedGroup) {
      setExistingPermissionErrorMessage("The group has already been added to the permissions");
    } else {
      setExistingPermissionErrorMessage("The user has already been added to the permissions");
    }
  };

  const handleRemoveCoOwner = (entity) => {
    templateData.value = {
      ...templateData.value,
      coOwners: templateData.value.coOwners.filter((coOwner) => coOwner !== entity),
    };
  };

  const handleRemoveSharedPermission = (entity) => {
    templateData.value = {
      ...templateData.value,
      sharedPermission: templateData.value.sharedPermission.filter(
        (sharedPermission) => sharedPermission !== entity
      ),
    };
  };

  return (
    <div className="k-d-flex k-flex-col k-border-gray k-border-solid k-h-11/12">
      <div className="k-p-2.5 k-overflow-y-auto k-flex-1">
        <h2 className="k-my-0">Permitted Users:</h2>
        <div className="k-d-flex k-flex-col">
          {coOwners && (
            <div className="k-filter">
              <h3 className="k-my-1">Co-Owners:</h3>
              <ul>
                {coOwners.map((coOwner) => (
                  <li key={uuidv4()}>
                    <div className="align-columns">
                      <ColorChangingIcon
                        icon={faXmark}
                        initialPrimaryColor="#D6562A"
                        onClick={() => {
                          if (isDataset) {
                            removeCoOwner(coOwner);
                          } else {
                            handleRemoveCoOwner(coOwner);
                          }
                        }}
                      />
                      {coOwner.isUserGroup || coOwner.userGroupId ? (
                        <div className="k-ml-2 k-d-flex k-align-items-center">
                          <span style={{ fontSize: "16px", marginRight: "8px" }}>📁</span>
                          <span>{coOwner.user} (User Group)</span>
                        </div>
                      ) : (
                        <mgt-person
                          person-query={coOwner.user}
                          view="oneline"
                        />
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {sharedPermission && (
            <div className="k-filter">
              <h3 className="k-my-1">Shared Permissions:</h3>
              <ul>
                {sharedPermission.map((sharedPermission) => (
                  <li key={uuidv4()}>
                    <div className="align-columns">
                      <ColorChangingIcon
                        icon={faXmark}
                        initialPrimaryColor="#D6562A"
                        onClick={() => {
                          if (isDataset) {
                            removeSharedPermission(sharedPermission);
                          } else {
                            handleRemoveSharedPermission(sharedPermission);
                          }
                        }}
                      />
                      {sharedPermission.isUserGroup || sharedPermission.userGroupId ? (
                        <div className="k-ml-2 k-d-flex k-align-items-center">
                          <span style={{ fontSize: "16px", marginRight: "8px" }}>📁</span>
                          <span>
                            {sharedPermission.entity} (User Group) - {sharedPermission.permission}
                          </span>
                        </div>
                      ) : (
                        <>
                          <mgt-person
                            person-query={sharedPermission.entity}
                            view="oneline"
                          ></mgt-person>{" "}
                          - {sharedPermission.permission}
                        </>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
      <div className="separator" />
      <div className="k-overflow-y-auto k-mb-2.5 k-flex-2">
        <div className="k-p-2.5 k-b-">
          <Form
            render={() => {
              return (
                <FormElement style={{ width: "75%" }}>
                  <div className="page-header">
                    <h1 className="k-my-0">Add Permission</h1>
                  </div>
                  <div className="k-my-3">
                    <PeoplePickerComponent
                      name="peoplePicker"
                      selectedPeopleState={selectedPeople}
                      setSelectedPeople={(val) => {
                        setExistingPermissionErrorMessage(null);
                        setSelectedPeople(val);
                      }}
                      singleSelect={singleSelect}
                      personType={"person"}
                      label="Select User or Group"
                      optional="false"
                      enableGroups={true}
                    />
                  </div>
                  <div>
                    <div className="k-mb-1">
                      <Checkbox
                        checked={isCoOwner}
                        label={"Co-Owner"}
                        onChange={() => {
                          setIsCoOwner(true);
                          setCanModify(false);
                          setCanView(false);
                          setCanMakeEntry(false);
                          setCanViewAuthorOnly(false);
                        }}
                      />
                    </div>
                    <div className="k-mb-1">
                      <Checkbox
                        checked={canModify}
                        label={"Modify"}
                        onChange={() => {
                          setIsCoOwner(false);
                          setCanModify(true);
                          setCanView(false);
                          setCanMakeEntry(false);
                          setCanViewAuthorOnly(false);
                        }}
                      />
                    </div>
                    <div className="k-mb-1">
                      <Checkbox
                        checked={canView}
                        label={"View"}
                        onChange={() => {
                          setIsCoOwner(false);
                          setCanModify(false);
                          setCanView(true);
                          setCanMakeEntry(false);
                          setCanViewAuthorOnly(false);
                        }}
                      />
                    </div>
                    {!isDataset && (
                      <>
                        <div className="k-mb-1">
                          <Checkbox
                            checked={canMakeEntry}
                            label={"Make Entry"}
                            onChange={() => {
                              setIsCoOwner(false);
                              setCanModify(false);
                              setCanView(false);
                              setCanMakeEntry(true);
                              setCanViewAuthorOnly(false);
                            }}
                          />
                        </div>
                        <div className="k-mb-1">
                          <Checkbox
                            checked={canViewAuthorOnly}
                            label={"Make Entry - Restricted View"}
                            onChange={() => {
                              setIsCoOwner(false);
                              setCanModify(false);
                              setCanView(false);
                              setCanMakeEntry(false);
                              setCanViewAuthorOnly(true);
                            }}
                          />
                        </div>
                      </>
                    )}
                  </div>
                  <div className="k-form-buttons">
                    <Button
                      type="button"
                      primary="true"
                      onClick={handleSubmit}
                      disabled={submitDisabled()}
                      themeColor="primary"
                    >
                      Add
                    </Button>
                    {existingPermissionErrorMessage && (
                      <div style={{ color: "red", marginTop: "4px", fontSize: "0.875rem" }}>
                        {existingPermissionErrorMessage}
                      </div>
                    )}
                  </div>
                </FormElement>
              );
            }}
          />
        </div>
        <div className="separator" />
        <div className="k-mt-2.5 k-mb-2.5 k-p-2.5">
          {!isDataset && (
            <>
              <div>
                <strong>Co-Owner: </strong>Can modify permissions, edit template, as well as modify,
                view, and make entries.
              </div>
              <div>
                <strong>Modify Template: </strong>Can edit template.
              </div>
              <div>
                <strong>View: </strong>Can view template and entries. Cannot edit template or make
                entries.
              </div>
              <div>
                <strong>Make Entry: </strong>Can make entries. Cannot edit template. Can view all
                entries.
              </div>
              <div>
                <strong>Make Entry - Restricted View: </strong>Can make entries. Cannot edit
                template. Can only view entries that they have authored.
              </div>
            </>
          )}
          {isDataset && (
            <>
              <div>
                <strong>Co-Owner: </strong>Can modify permissions, edit dataset
              </div>
              <div>
                <strong>Modify Dataset: </strong>Can edit dataset.
              </div>
              <div>
                <strong>View: </strong>Can view dataset. Cannot edit dataset.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SharedPerms;

const PeoplePickerComponent = (fieldRenderProps) => {
  const {
    validationMessage,
    touched,
    id,
    label,
    valid,
    disabled,
    hint,
    optional,
    selectedPeopleState,
    setSelectedPeople,
    selectionMode,
    personType,
    userType,
    ...others
  } = fieldRenderProps;
  const showValidationMessage = touched && validationMessage;
  const showHint = !showValidationMessage && hint;
  const hintId = showHint ? `${id}_hint` : "";
  const errorId = showValidationMessage ? `${id}_error` : "";
  const labelId = label ? `${id}_label` : "";

  const handleSelectionChange = (event) => {
    if (event.target.isGroupSelection && event.target.selectedGroup) {
      setSelectedPeople((prevSelectedPeople) => ({
        ...prevSelectedPeople,
        [others.name]: event.target.selectedPeople,
        [`${others.name}_display`]: [event.target.selectedGroup],
        [`${others.name}_group`]: event.target.selectedGroup,
      }));
    } else {
      setSelectedPeople((prevSelectedPeople) => ({
        ...prevSelectedPeople,
        [others.name]: event.target.selectedPeople,
        [`${others.name}_display`]: event.target.selectedPeople,
        [`${others.name}_group`]: null,
      }));
    }
  };

  return (
    <FieldWrapper>
      <Label
        id={labelId}
        editorId={id}
        editorValid={valid}
        editorDisabled={disabled}
      >
        {`${label}:` + (optional ? " *" : " (optional)")}
      </Label>
      <CustomPeoplePicker
        id={id}
        name={others.name}
        valid={valid}
        disabled={disabled}
        selectedPeople={
          selectedPeopleState?.[`${others.name}_display`] ||
          selectedPeopleState?.[others.name] ||
          []
        }
        selectionMode={selectionMode}
        onChange={handleSelectionChange}
        enableGroups={others.enableGroups}
      />
      {showHint && <Hint id={hintId}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  );
};
